import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query'
import { useSetAtom } from 'jotai'
import { useCallback } from 'react'
import { toast } from '~components/ui/toast'
import Pet from '~models/pet'
import PetRepository from '~repositories/impl/pet-repository'
import loadingAtom from '~store/loading'
import Mixpanel from '~utils/mixpanel'
import { APP_QUERY_KEYS } from './query-keys'

const useUpdatePet = (options?: UseMutationOptions<Pet.Level1, Error, Pet.Level1>) => {
  const queryClient = useQueryClient()
  const setLoading = useSetAtom(loadingAtom)

  const { mutate } = useMutation({
    mutationFn: PetRepository.updatePet,
    onMutate: (req: Pet.ILevel1) => {
      options?.onMutate?.(req)
      setLoading(true)
    },
    onSuccess: (res, req, ctx) => {
      options?.onSuccess?.(res, req, ctx)
      queryClient.setQueryData(APP_QUERY_KEYS.myPets, (old: Pet.Level1[]) => {
        return old.map(pet => {
          if (pet.id === req.id) {
            pet.incomplete_fields = []
            return res
          }
          return pet
        })
      })
      Mixpanel.logEvent('pet: update_completed')
      toast.open({
        type: 'success',
        message: `Changes saved successfully.`,
      })
    },
    onSettled: (data, error, variables, context) => {
      options?.onSettled?.(data, error, variables, context)
      setLoading(false)
    },
  })

  const updatePet = useCallback(
    (pet: Pet.Level1) => {
      mutate(pet)
    },
    [mutate]
  )

  return updatePet
}

export default useUpdatePet
